'use client';

import { Accordion, AccordionItem, Link } from '@/components/ui';
import { HeaderNavigationItem } from '@/configs/header/header.config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { resolveStudioPageHref } from '@/utils/resolve-studio-page-href';

type Props = {
  onNavigate?: () => void;
};

const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';

const resolveTriggerButtonLabel = (
  triggerButtonUrl: string,
  item: HeaderNavigationItem,
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const href = normalizePath(resolveStudioPageHref(triggerButtonUrl));

  if (href === '/products') {
    return t?.products.allProductsPage ?? t?.products.productsPage ?? 'Products';
  }

  const categorySlug = href.match(/^\/products\/([^/]+)$/)?.[1];
  if (categorySlug) {
    const category = (item.categories ?? []).find(c => c?.meta?.url === categorySlug);
    if (category?.name) return category.name;
  }

  const productSlug = href.match(/^\/product\/([^/]+)$/)?.[1];
  if (productSlug) {
    const product = (item.products ?? []).find(p => p?.meta?.url === productSlug);
    if (product?.name) return product.name;
  }

  return t?.products.productsPage ?? 'Products';
};

const MobileNavigationItems = ({ onNavigate }: Props) => {
  const headerContent = useHeaderContent();
  const resolveThemeColor = useResolveThemeColor();
  const { t } = useTranslation();
  /** Drawer uses a light surface; header nav tokens are often light-on-dark and would be invisible here. */
  const listFg = resolveThemeColor('foreground');

  const renderTitleLink = (href: string, label: string) => (
    <Link href={href} noStyle className="w-full" onClick={onNavigate}>
      <div className="w-full">{label}</div>
    </Link>
  );

  const renderTriggerLink = (item: HeaderNavigationItem) => {
    const triggerButtonUrl = item.triggerButtonUrl?.trim();
    if (!triggerButtonUrl) return null;

    const href = resolveStudioPageHref(triggerButtonUrl);
    const label = resolveTriggerButtonLabel(triggerButtonUrl, item, t);

    return (
      <Link
        key={`trigger-${item.label}-${href}`}
        noStyle
        href={href}
        className="font-medium transition-opacity duration-300 hover:opacity-70"
        onClick={onNavigate}
      >
        {label}
      </Link>
    );
  };

  const renderNavigationItem = (item: HeaderNavigationItem) => {
    if (item.type === 'link') {
      return (
        <AccordionItem
          key={`${item.type}-${item.label}`}
          aria-label={item.label}
          title={renderTitleLink(resolveStudioPageHref(item.pageUrl), item.label)}
          hideIndicator
          classNames={{ content: 'hidden' }}
        />
      );
    }

    if (item.type === 'category-link') {
      const firstCategory = (item.categories ?? []).find(category => category);
      const href = firstCategory?.meta?.url ? `/products/${firstCategory.meta.url}` : '/products';
      const label = item.label || firstCategory?.name || 'Category';

      return (
        <AccordionItem
          key={`${item.type}-${item.label}`}
          aria-label={label}
          title={renderTitleLink(href, label)}
          hideIndicator
          classNames={{ content: 'hidden' }}
        />
      );
    }

    if (item.type === 'categories-dropdown') {
      return (
        <AccordionItem
          key={`${item.type}-${item.label}`}
          aria-label={item.label}
          title={item.label}
        >
          <div className="flex flex-col gap-3 pl-4">
            {renderTriggerLink(item)}
            {(item.categories ?? [])
              .filter(category => category?.meta?.url)
              .map(category => (
                <Link
                  key={category!.id}
                  noStyle
                  href={`/products/${category!.meta.url}`}
                  className="transition-opacity duration-300 hover:opacity-70"
                  onClick={onNavigate}
                >
                  {category!.name}
                </Link>
              ))}
          </div>
        </AccordionItem>
      );
    }

    return (
      <AccordionItem key={`${item.type}-${item.label}`} aria-label={item.label} title={item.label}>
        <div className="flex flex-col gap-3 pl-4">
          {renderTriggerLink(item)}
          {(item.products ?? [])
            .filter(product => product?.meta?.url)
            .map(product => (
              <Link
                key={product!.id}
                noStyle
                withoutLocale
                href={`/${product!.locale}/product/${product!.meta.url}`}
                className="transition-opacity duration-300 hover:opacity-70"
                onClick={onNavigate}
              >
                {product!.name}
              </Link>
            ))}
        </div>
      </AccordionItem>
    );
  };

  return (
    <Accordion
      variant="light"
      style={{ color: listFg }}
      itemClasses={{
        base: 'text-inherit',
        title: '!text-inherit',
        heading: '!text-inherit',
        titleWrapper: '!text-inherit',
        trigger: '!text-inherit',
        indicator: '!text-inherit',
        content: '!text-inherit [&_a]:!text-inherit',
      }}
    >
      {headerContent.navigationItems.map(renderNavigationItem)}
    </Accordion>
  );
};

export default MobileNavigationItems;
